import { useEffect, useId, useRef, type ReactElement } from 'react';
import type { User } from '@chatapp-inbox/shared';
import { ROLE_LABELS } from '../../auth/messages';
import { Button } from '../details/Button';
import { EditUserPanel } from './EditUserPanel';
import styles from './UserCard.module.css';

interface UserCardProps {
  user: User;
  /** É a pessoa que está usando a tela. */
  isYou: boolean;
  viewerId: string;
  editing: boolean;
  onToggleEdit: () => void;
  /** Pede o foco no título (por exemplo, logo depois de o usuário ser criado). */
  focusRequested: boolean;
  onFocusHandled: () => void;
}

/**
 * Um usuário da lista. O nome, o e-mail e o papel são sempre TEXTO PURO (nunca HTML), com
 * `dir="auto"` e quebra de palavras longas. A situação é dita em palavras ("Ativo" / "Desativado"),
 * nunca só por cor.
 */
export function UserCard({
  user,
  isYou,
  viewerId,
  editing,
  onToggleEdit,
  focusRequested,
  onFocusHandled,
}: UserCardProps): ReactElement {
  const uid = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const panelId = `${uid}-panel`;

  useEffect(() => {
    if (!focusRequested) return;
    headingRef.current?.focus();
    onFocusHandled();
  }, [focusRequested, onFocusHandled]);

  return (
    <li className={styles.card} data-user-id={user.id}>
      <div className={styles.summary}>
        <div className={styles.identity}>
          {/* `tabIndex -1`: recebe foco por código (cartão novo, usuário desativado ou reativado). */}
          <h3 ref={headingRef} tabIndex={-1} className={styles.name}>
            <span dir="auto">{user.name}</span>
            {isYou && <span className={styles.you}> (você)</span>}
          </h3>
          <dl className={styles.facts}>
            <div className={`${styles.fact} ${styles.wide}`}>
              <dt className={styles.term}>E-mail</dt>
              <dd dir="auto" className={styles.value}>
                {user.email}
              </dd>
            </div>
            <div className={styles.fact}>
              <dt className={styles.term}>Papel</dt>
              <dd className={styles.value}>{ROLE_LABELS[user.role]}</dd>
            </div>
            <div className={styles.fact}>
              <dt className={styles.term}>Situação</dt>
              <dd className={styles.value}>
                <span className={user.active ? styles.active : styles.inactive}>
                  {user.active ? 'Ativo' : 'Desativado'}
                </span>
              </dd>
            </div>
          </dl>
        </div>
        <Button
          aria-label={editing ? `Fechar edição de ${user.name}` : `Editar ${user.name}`}
          aria-controls={editing ? panelId : undefined}
          onClick={onToggleEdit}
        >
          {editing ? 'Fechar' : 'Editar'}
        </Button>
      </div>
      {editing && (
        <EditUserPanel
          id={panelId}
          user={user}
          viewerId={viewerId}
          focusHeading={() => headingRef.current?.focus()}
        />
      )}
    </li>
  );
}
